import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import translations, { Language } from './translations';
import analytics from '../lib/analytics';

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number | undefined>) => string;
  direction: 'ltr' | 'rtl';
}

const getDirection = (language: Language): 'ltr' | 'rtl' => {
  return language === 'he' ? 'rtl' : 'ltr';
};

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export function TranslationProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    if (saved === 'he' || saved === 'en') return saved;
    // First visit: detect from browser locale, default to 'he'
    const browserLang = navigator.language?.toLowerCase() ?? '';
    return browserLang.startsWith('he') ? 'he' : browserLang.startsWith('en') ? 'en' : 'he';
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = getDirection(lang);
    // Dispatch event for other potential listeners
    window.dispatchEvent(new CustomEvent('languageChange', { detail: { language: lang } }));
  }, []);

  const t = useCallback((key: string, params?: Record<string, string | number | undefined>): string => {
    let text = translations[language][key];
    if (!text) {
      console.warn(`Translation key not found: ${key}`);
      return key; // Fallback to key if not found
    }
    // Replace parameterized placeholders like {{apartment}}
    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        text = text.replace(new RegExp(`{{\\s*${param}\\s*}}`, 'g'), String(value ?? ''));
      });
    }
    return text;
  }, [language]);

  // Memoize context value to prevent unnecessary re-renders
  const direction = getDirection(language);
  const value = useMemo(
    () => ({ language, setLanguage, t, direction }),
    [language, setLanguage, t, direction]
  );

  useEffect(() => {
    // Update document language and direction attributes
    document.documentElement.lang = language;
    document.documentElement.dir = getDirection(language);
    // P3.3 + P3.5: mirror current language as a GA4 user property so reports
    // can be sliced by he-IL vs en-US funnel. Safe to call repeatedly.
    analytics.setUserProperties({ language });
  }, [language]);

  // Listen for language changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'language' && (e.newValue === 'en' || e.newValue === 'he')) {
        setLanguageState(e.newValue as Language);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}
